/**
 * Safe clipboard utility that works around browser permission restrictions
 * Falls back to older document.execCommand method when navigator.clipboard is blocked
 */

export async function copyToClipboard(text: string): Promise<boolean> {
  // Try modern clipboard API first
  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (error: any) {
      // Log as info instead of warning - permissions policy blocks are expected
      if (error.name === 'NotAllowedError') {
        console.log('ℹ️ Clipboard API blocked by permissions policy, using fallback');
      } else {
        console.log('ℹ️ Clipboard API failed:', error.name, '- using fallback');
      }
      // Fall through to fallback method
    }
  }

  // Fallback to document.execCommand (works in more contexts)
  try {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    
    const successful = document.execCommand('copy');
    document.body.removeChild(textArea);
    
    if (!successful) {
      console.warn('⚠️ Fallback copy method also failed');
    }
    
    return successful;
  } catch (error) {
    console.error('❌ All clipboard methods failed:', error);
    return false;
  }
}
